import React from "react";
import { Link } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  User,
  Shield,
  ClipboardList,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { useSupervisorStats, useSecurityStats } from "../adapters/Requests";

const MinimalTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white px-3 py-2 rounded border border-gray-100 shadow-lg z-50">
        <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
          {payload[0].name}
        </p>
        <p className="text-sm font-bold text-gray-900">{payload[0].value}</p>
      </div>
    );
  }
  return null;
};

const ChartCard = ({ title, children, footerData, link, linkText }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col h-full shadow-sm hover:shadow-md transition-shadow duration-200">
    <div className="mb-4 flex justify-between items-center">
      <h3 className="text-sm font-bold text-gray-700">{title}</h3>
      {link && (
        <Link
          to={link}
          className="text-[10px] font-bold text-emerald-600 hover:text-emerald-700 uppercase tracking-wide"
        >
          {linkText} &rarr;
        </Link>
      )}
    </div>
    <div className="flex-1 min-h-[180px] w-full relative">
      <ResponsiveContainer width="100%" height="100%" className="!outline-none">
        {children}
      </ResponsiveContainer>
    </div>
    {footerData && (
      <div className="mt-4 pt-3 border-t border-gray-50 flex flex-wrap gap-3 justify-center">
        {footerData.map((item, idx) => (
          <div key={idx} className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-[10px] font-semibold text-gray-500 uppercase">
              {item.name}
            </span>
            <span className="text-[10px] font-bold text-gray-800">
              {item.value}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

function SmallStatGrid({ stats }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
      {stats.map((stat, index) => (
        <div
          key={index}
          className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 bg-slate-50 shadow-sm cursor-default select-none h-24 transition-colors ${
            stat.alert ? "border-red-100" : "border-white"
          }`}
        >
          <div
            className={`h-7 w-7 rounded-md flex items-center justify-center mb-2 ${
              stat.alert ? "text-red-500" : "text-emerald-500"
            }`}
          >
            <stat.icon size={18} strokeWidth={2.5} />
          </div>
          <h3
            className={`text-lg font-bold leading-none ${
              stat.alert ? "text-red-700" : "text-slate-700"
            }`}
          >
            {stat.value}
          </h3>
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mt-1.5">
            {stat.label}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function SupervisorActionCenter({ userRole, dateRange }) {
  const axisStyle = { fill: "#9ca3af", fontSize: 10, fontWeight: 500 };
  const gridStyle = { stroke: "#f3f4f6", strokeDasharray: "3 3" };

  if (userRole === "security") {
    const { data, isLoading } = useSecurityStats(dateRange);

    if (isLoading)
      return (
        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mx-auto mt-10" />
      );

    const active = data?.active_interns || 0;
    const issues = data?.flagged_issues || 0;
    const totalAccess = data?.total_access || 0;

    const securityStats = [
      { label: "On-Site", value: active, icon: User },
      { label: "Flagged", value: issues, icon: Shield, alert: issues > 0 },
    ];

    const chartData = [
      { name: "Cleared", value: active, color: "#10b981" },
      { name: "Flagged", value: issues, color: "#ef4444" },
    ].filter((d) => d.value > 0);

    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <SmallStatGrid stats={securityStats} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ChartCard
            title="Site Safety Status"
            link="/security"
            linkText="Manage Issues"
            footerData={chartData}
          >
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<MinimalTooltip />} />
            </PieChart>
          </ChartCard>

          <ChartCard
            title="Access Volume (Today)"
            link="/security"
            linkText="View Logs"
          >
            <BarChart
              data={[{ name: "Total Entries", value: totalAccess }]}
              margin={{ top: 20, right: 20, left: -20, bottom: 0 }}
            >
              <CartesianGrid {...gridStyle} vertical={false} />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={axisStyle}
              />
              <YAxis axisLine={false} tickLine={false} tick={axisStyle} />
              <Tooltip
                content={<MinimalTooltip />}
                cursor={{ fill: "transparent" }}
              />
              <Bar
                dataKey="value"
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
                barSize={50}
              />
            </BarChart>
          </ChartCard>
        </div>
      </div>
    );
  }

  if (userRole === "supervisor") {
    const { data, isLoading } = useSupervisorStats(dateRange);

    if (isLoading)
      return (
        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mx-auto mt-10" />
      );

    const total = data?.total || 0;
    const completed = data?.completed || 0;
    const pending = data?.pending || 0;

    const supervisorStats = [
      { label: "My Interns", value: total, icon: User },
      {
        label: "Pending Reviews",
        value: pending,
        icon: ClipboardList,
        alert: pending > 0,
      },
      { label: "Completed", value: completed, icon: CheckCircle },
    ];

    const assessmentData = [
      { name: "Completed", value: completed, color: "#10b981" },
      { name: "Pending", value: pending, color: "#cbd5e1" },
    ].filter((d) => d.value > 0);

    const workloadData = [
      { name: "Total Assigned", value: total },
      { name: "Action Needed", value: pending },
    ];

    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <SmallStatGrid stats={supervisorStats} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ChartCard
            title="Assessment Progress"
            link="/supervision"
            linkText="Start Reviews"
            footerData={assessmentData}
          >
            <PieChart>
              <Pie
                data={assessmentData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                {assessmentData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<MinimalTooltip />} />
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                <tspan
                  x="50%"
                  dy="-0.5em"
                  fontSize="24"
                  fontWeight="bold"
                  fill="#1f2937"
                >
                  {total > 0 ? Math.round((completed / total) * 100) : 0}%
                </tspan>
                <tspan
                  className="mt-2"
                  x="50%"
                  dy="2.0em"
                  fontSize="10"
                  fill="#9ca3af"
                >
                  REVIEWED
                </tspan>
              </text>
            </PieChart>
          </ChartCard>

          <ChartCard
            title="Intern Workload"
            link="/supervision"
            linkText="View Roster"
          >
            <BarChart
              data={workloadData}
              margin={{ top: 20, right: 20, left: -20, bottom: 0 }}
            >
              <CartesianGrid {...gridStyle} vertical={false} />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={axisStyle}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={axisStyle}
                allowDecimals={false}
              />
              <Tooltip
                content={<MinimalTooltip />}
                cursor={{ fill: "transparent" }}
              />
              <Bar
                dataKey="value"
                fill="#059669"
                radius={[4, 4, 0, 0]}
                barSize={40}
              />
            </BarChart>
          </ChartCard>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8 bg-white border border-slate-200 rounded-lg shadow-sm">
      <h2 className="text-2xl font-bold text-slate-800 mb-2">
        System Operational
      </h2>
      <p className="text-slate-500 max-w-md">
        Welcome to your dashboard. No specific role actions detected.
      </p>
    </div>
  );
}
